import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { YBtn, YTabs } from '../../../components/yool/YoolPrimitives';
import { getPolizaById, createPoliza, updatePoliza } from '../../../services/divinaPolizasService';
import { getPreciosBase } from '../../../services/supabase/planesSupabaseService';
import { getEmpresaPlanHabilitadoPorPoliza } from '../../../services/empresasPlanesService';
import { toLocalDateTimeInput, toLocalMidnightInput } from '../../../styles/tokens/formatters';
import PasoCompania from './wizard-steps/PasoCompania';
import PasoProducto from './wizard-steps/PasoProducto';
import PasoPlan from './wizard-steps/PasoPlan';
import PasoContratante from './wizard-steps/PasoContratante';
import PasoPoliza from './wizard-steps/PasoPoliza';
import PasoEmpresaPlan from './wizard-steps/PasoEmpresaPlan';

// Contratante y Empresa se fusionaron en un solo paso, al final: pedido del
// usuario tras probar el wizard — la póliza ya existe recién al terminar
// el paso "Póliza", así que el vínculo real con la empresa siempre tuvo que
// ir después; juntar ahí también al contratante evita un paso intermedio
// que interrumpía el flujo compañía → producto → plan → póliza.
const PASOS = [
  { id: 'compania', label: 'Compañía' },
  { id: 'producto', label: 'Producto' },
  { id: 'plan', label: 'Plan' },
  { id: 'poliza', label: 'Póliza' },
  { id: 'empresa', label: 'Contratante y Empresa' },
];

const ESTADO_INICIAL = {
  compania_id: '', producto_id: '', plan_id: '', contratante_id: '',
  poliza: {
    numero_poliza: '', numero_contrato: '', url_poliza: '',
    fecha_emision: '', fecha_inicio: '', fecha_termino: '',
    estado: 'vigente', ramo_id: '', prima_total: '', moneda: '',
  },
  empresa_id: '', empresa_plan_habilitado_id: '',
  poliza_id: null,
};

export default function PolizaColectivaWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [pasoIdx, setPasoIdx] = useState(0);
  const [estado, setEstado] = useState(ESTADO_INICIAL);
  const [nombres, setNombres] = useState({ compania: '', plan: '' });
  const [saving, setSaving] = useState(false);

  // Función de sincronización real de tramos-de-precio (assignPolizaToPrecios/
  // clearPolizaFromPrecios), registrada por PasoPoliza (paso 5) — se
  // re-registra cada vez que sus datos internos cambian, así que este ref
  // siempre guarda la versión más fresca sin forzar un re-render del wizard.
  const syncTramosRef = useRef(null);

  useEffect(() => {
    if (!id) return;
    getPolizaById(id).then(async (poliza) => {
      // Tramos ya asignados a esta póliza — sin esto, el paso de póliza
      // arrancaría con la selección vacía y al guardar desasignaría en
      // silencio los tramos de precio reales que la póliza ya tenía.
      let tramosAsignados = [];
      if (poliza.plan_id) {
        try {
          const precios = await getPreciosBase(poliza.plan_id);
          tramosAsignados = (precios || [])
            .filter((p) => String(p.poliza_colectiva_id) === String(id))
            .map((p) => p.id);
        } catch (error) {
          console.warn('Error al obtener tramos de precio para precargar:', error);
        }
      }

      // Empresa + plan habilitado ya vinculados a esta póliza — sin esto, el
      // paso 6 arrancaba siempre en blanco al reabrir una póliza en modo
      // edición, aunque el vínculo ya existiera de verdad (bug real reportado).
      let vinculoEmpresa = null;
      try {
        vinculoEmpresa = await getEmpresaPlanHabilitadoPorPoliza(id);
      } catch (error) {
        console.warn('Error al obtener el vínculo empresa/plan habilitado para precargar:', error);
      }

      setEstado((prev) => ({
        ...prev,
        compania_id: poliza.compania_id ? String(poliza.compania_id) : '',
        // poliza.plan viene del embed plan:planes(id, nombre, producto_id) de
        // getPolizaById — sin esto, PasoPlan (que filtra por productoId) muestra
        // "Selecciona un producto primero" en modo edición aunque plan_id ya
        // esté precargado más abajo.
        producto_id: poliza.plan?.producto_id ? String(poliza.plan.producto_id) : '',
        plan_id: poliza.plan_id ? String(poliza.plan_id) : '',
        contratante_id: poliza.contratante_id ? String(poliza.contratante_id) : '',
        poliza: {
          numero_poliza: poliza.numero_poliza || '',
          numero_contrato: poliza.numero_contrato || '',
          url_poliza: poliza.url_poliza || '',
          fecha_emision: poliza.fecha_emision ? toLocalDateTimeInput(poliza.fecha_emision) : '',
          fecha_inicio: poliza.fecha_inicio ? toLocalMidnightInput(poliza.fecha_inicio) : '',
          fecha_termino: poliza.fecha_termino ? toLocalMidnightInput(poliza.fecha_termino) : '',
          estado: poliza.estado || 'vigente',
          ramo_id: poliza.ramo_id || '',
          prima_total: poliza.prima_total || '',
          moneda: poliza.moneda || '',
          tramos_seleccionados: tramosAsignados,
          // Sin precargar estos dos, guardarPoliza() los enviaría como
          // undefined/ausentes y updatePoliza() los sobrescribiría con null
          // (createPoliza/updatePoliza usan `?? null`), borrando en silencio
          // configuración real ya existente (bug encontrado en review final).
          wallet_back_config: poliza.wallet_back_config || null,
          metadata: poliza.metadata || null,
        },
        empresa_id: vinculoEmpresa?.empresa_id ? String(vinculoEmpresa.empresa_id) : '',
        empresa_plan_habilitado_id: vinculoEmpresa?.id ? String(vinculoEmpresa.id) : '',
        poliza_id: id,
      }));
      setNombres({ compania: poliza.compania?.nombre_corto || '', plan: poliza.plan?.nombre || '' });
    });
  }, [id]);

  const patch = (p) => setEstado((prev) => ({ ...prev, ...p }));
  const patchPoliza = (p) => setEstado((prev) => ({ ...prev, poliza: { ...prev.poliza, ...p } }));

  // Inverso de toLocalDateTimeInput/toLocalMidnightInput (mismo patrón que
  // toIsoOrNull en ColectivePolizasForm.jsx): los inputs datetime-local/date
  // guardan un string local sin offset — sin esto, se envía tal cual a la
  // DB y Postgres lo interpreta como UTC, corriendo la fecha real guardada.
  const toIsoOrNull = (val) => {
    if (!val) return null;
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d.toISOString();
  };

  const guardarPoliza = async () => {
    const payload = {
      ...estado.poliza,
      fecha_emision: toIsoOrNull(estado.poliza.fecha_emision),
      fecha_inicio: toIsoOrNull(estado.poliza.fecha_inicio),
      fecha_termino: toIsoOrNull(estado.poliza.fecha_termino),
      plan_id: estado.plan_id,
      compania_id: estado.compania_id,
      contratante_id: estado.contratante_id || null,
    };
    try {
      setSaving(true);
      const resultado = estado.poliza_id
        ? await updatePoliza(estado.poliza_id, payload)
        : await createPoliza(payload);
      // La póliza ya existe de verdad (tiene id) — recién ahora se puede
      // sincronizar la asignación de tramos de precio contra ella.
      if (syncTramosRef.current) await syncTramosRef.current(resultado.id);
      setEstado((prev) => ({ ...prev, poliza_id: resultado.id }));
      toast.success(isEditing ? 'Póliza actualizada' : 'Póliza creada');
      return resultado.id;
    } catch (e) {
      toast.error(e.message || 'Error al guardar la póliza');
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const siguiente = async () => {
    if (PASOS[pasoIdx].id === 'poliza') {
      await guardarPoliza();
    }
    if (PASOS[pasoIdx].id === 'empresa') {
      // El contratante se elige en este mismo paso (fusionado con Empresa),
      // después de que la póliza ya fue creada/actualizada en el paso
      // anterior — hay que volver a guardar para persistir contratante_id.
      await guardarPoliza();
    }
    if (pasoIdx < PASOS.length - 1) setPasoIdx(pasoIdx + 1);
    else navigate('/product_config/polizas');
  };

  const anterior = () => setPasoIdx(Math.max(0, pasoIdx - 1));

  const irAPaso = (stepId) => {
    const idx = PASOS.findIndex((p) => p.id === stepId);
    if (idx <= pasoIdx) setPasoIdx(idx); // solo permite volver a pasos ya visitados
  };

  const renderPaso = () => {
    switch (PASOS[pasoIdx].id) {
      case 'compania':
        return <PasoCompania value={estado} onChange={patch} />;
      case 'producto':
        return <PasoProducto companiaId={estado.compania_id} value={estado} onChange={patch} />;
      case 'plan':
        return <PasoPlan productoId={estado.producto_id} value={estado} onChange={patch} />;
      case 'poliza':
        return (
          <PasoPoliza
            planId={estado.plan_id}
            companiaNombre={nombres.compania}
            planNombre={nombres.plan}
            value={estado.poliza}
            onChange={patchPoliza}
            onRegisterSyncTramos={(fn) => { syncTramosRef.current = fn; }}
          />
        );
      case 'empresa':
        return (
          <>
            <PasoContratante value={estado} onChange={patch} />
            <PasoEmpresaPlan
              polizaId={estado.poliza_id}
              value={estado}
              onChange={patch}
              nombres={nombres}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="y-detail-card">
      <div className="y-detail-head">
        <div>
          <div className="y-detail-head-eyebrow">Póliza colectiva</div>
          <div className="y-detail-head-title">
            {isEditing ? 'Editar Póliza Colectiva' : 'Nueva Póliza Colectiva'}
          </div>
        </div>
      </div>

      <YTabs tabs={PASOS} value={PASOS[pasoIdx].id} onChange={irAPaso} />

      <div className="y-form-body">
        {renderPaso()}
      </div>

      <div className="y-form-footer">
        <div className="y-form-footer-actions">
          <YBtn variant="outline" onClick={anterior} disabled={pasoIdx === 0}>Atrás</YBtn>
          <YBtn onClick={siguiente} disabled={saving}>
            {pasoIdx === PASOS.length - 1 ? 'Finalizar' : 'Siguiente'}
          </YBtn>
        </div>
      </div>
    </div>
  );
}
